import React, { useState, useEffect } from 'react';

/**
 * UserAvatar
 * ==========
 * Displays the user's profile picture with a graceful fallback chain:
 *   1. Uploaded profile picture (from backend /uploads/profile_pictures/...)
 *   2. Google profile picture (full URL stored by backend during Google sign-in)
 *   3. Gravatar (from the user's email, if available)
 *   4. Silhouette SVG (final fallback)
 *
 * All images have onError handlers that cascade to the next fallback.
 */

const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const sizes = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-24 h-24',
    '3xl': 'w-32 h-32',
  };

  const textSizes = {
    xs: 'text-[8px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-lg',
    '2xl': 'text-xl',
    '3xl': 'text-2xl',
  };

  const sizePx = { xs: 32, sm: 64, md: 80, lg: 96, xl: 128, '2xl': 192, '3xl': 256 }[size] || 80;
  const sizeClass = sizes[size] || sizes.md;
  const textSize = textSizes[size] || textSizes.md;

  const getInitials = (name) => {
    return (name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getProfilePictureUrl = () => {
    if (!user?.profile_picture) return null;
    const pic = user.profile_picture;
    // Full URL (Google profile pic, etc.)
    if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
    // Relative path — prepend API base URL
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) return `${envUrl}${pic}`;
    // Fallback: use current origin (same-origin requests)
    return `${window.location.origin}${pic}`;
  };

  // Compute Gravatar URL from email using async MD5 via Web Crypto API
  const profileUrl = getProfilePictureUrl();
  const email = user?.email;
  const [gravatarUrl, setGravatarUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!email || profileUrl) {
      setGravatarUrl(null);
      return;
    }
    // Use SubtleCrypto to compute MD5 — not supported for MD5 in most browsers,
    // so we use SHA-256 instead. Gravatar requires MD5, so we use a tiny
    // inline MD5 implementation.
    const computeMd5 = (str) => {
      // Minimal MD5 implementation (public domain, Joseph Myers)
      // https://www.myersdaily.org/joseph/javascript/md5-text-version.html
      function md5cycle(x, k) {
        var a = x[0], b = x[1], c = x[2], d = x[3];
        a = ff(a, b, c, d, k[0], 7, -680876936); d = ff(d, a, b, c, k[1], 12, -389564586); c = ff(c, d, a, b, k[2], 17, 606105819); b = ff(b, c, d, a, k[3], 22, -1044525330);
        a = ff(a, b, c, d, k[4], 7, -176418897); d = ff(d, a, b, c, k[5], 12, 1200080426); c = ff(c, d, a, b, k[6], 17, -1473231341); b = ff(b, c, d, a, k[7], 22, -45705983);
        a = ff(a, b, c, d, k[8], 7, 1770035416); d = ff(d, a, b, c, k[9], 12, -1958414417); c = ff(c, d, a, b, k[10], 17, -42063); b = ff(b, c, d, a, k[11], 22, -1990404162);
        a = ff(a, b, c, d, k[12], 7, 1804603682); d = ff(d, a, b, c, k[13], 12, -40341101); c = ff(c, d, a, b, k[14], 17, -1502002290); b = ff(b, c, d, a, k[15], 22, 1236535329);
        a = gg(a, b, c, d, k[1], 5, -165796510); d = gg(d, a, b, c, k[6], 9, -1069501632); c = gg(c, d, a, b, k[11], 14, 643717713); b = gg(b, c, d, a, k[0], 20, -373897302);
        a = gg(a, b, c, d, k[5], 5, -701558691); d = gg(d, a, b, c, k[10], 9, 38016083); c = gg(c, d, a, b, k[15], 14, -660478335); b = gg(b, c, d, a, k[4], 20, -405537848);
        a = gg(a, b, c, d, k[9], 5, 568446438); d = gg(d, a, b, c, k[14], 9, -1019803690); c = gg(c, d, a, b, k[3], 14, -187363961); b = gg(b, c, d, a, k[8], 20, 1163531501);
        a = gg(a, b, c, d, k[13], 5, -1444681467); d = gg(d, a, b, c, k[2], 9, -51403784); c = gg(c, d, a, b, k[7], 14, 1735328473); b = gg(b, c, d, a, k[12], 20, -1926607734);
        a = hh(a, b, c, d, k[5], 4, -378558); d = hh(d, a, b, c, k[8], 11, -2022574463); c = hh(c, d, a, b, k[11], 16, 1839030562); b = hh(b, c, d, a, k[14], 23, -35309556);
        a = hh(a, b, c, d, k[1], 4, -1530992060); d = hh(d, a, b, c, k[4], 11, 1272893353); c = hh(c, d, a, b, k[7], 16, -155497632); b = hh(b, c, d, a, k[10], 23, -1094730640);
        a = hh(a, b, c, d, k[13], 4, 681279174); d = hh(d, a, b, c, k[0], 11, -358537222); c = hh(c, d, a, b, k[3], 16, -722521979); b = hh(b, c, d, a, k[6], 23, 76029189);
        a = hh(a, b, c, d, k[9], 4, -640364487); d = hh(d, a, b, c, k[12], 11, -421815835); c = hh(c, d, a, b, k[15], 16, 530742520); b = hh(b, c, d, a, k[2], 23, -995338651);
        a = ii(a, b, c, d, k[0], 6, -198630844); d = ii(d, a, b, c, k[7], 10, 1126891415); c = ii(c, d, a, b, k[14], 15, -1416354905); b = ii(b, c, d, a, k[5], 21, -57434055);
        a = ii(a, b, c, d, k[12], 6, 1700485571); d = ii(d, a, b, c, k[3], 10, -1894986606); c = ii(c, d, a, b, k[10], 15, -1051523); b = ii(b, c, d, a, k[1], 21, -2054922799);
        a = ii(a, b, c, d, k[8], 6, 1873313359); d = ii(d, a, b, c, k[15], 10, -30611744); c = ii(c, d, a, b, k[6], 15, -1560198380); b = ii(b, c, d, a, k[13], 21, 1309151649);
        a = ii(a, b, c, d, k[4], 6, -145523070); d = ii(d, a, b, c, k[11], 10, -1120210379); c = ii(c, d, a, b, k[2], 15, 718787259); b = ii(b, c, d, a, k[9], 21, -343485551);
        x[0] = add32(a, x[0]); x[1] = add32(b, x[1]); x[2] = add32(c, x[2]); x[3] = add32(d, x[3]);
      }
      function cmn(q, a, b, x, s, t) { a = add32(add32(a, q), add32(x, t)); return add32((a << s) | (a >>> (32 - s)), b); }
      function ff(a, b, c, d, x, s, t) { return cmn((b & c) | ((~b) & d), a, b, x, s, t); }
      function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & (~d)), a, b, x, s, t); }
      function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t); }
      function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | (~d)), a, b, x, s, t); }
      function md51(s) {
        var n = s.length, state = [1732584193, -271733879, -1732584194, 271733878], i;
        for (i = 64; i <= s.length; i += 64) { md5cycle(state, md5blk(s.substring(i - 64, i))); }
        s = s.substring(i - 64);
        var tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        for (i = 0; i < s.length; i++) tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
        tail[i >> 2] |= 0x80 << ((i % 4) << 3);
        if (i > 55) { md5cycle(state, tail); for (i = 0; i < 16; i++) tail[i] = 0; }
        tail[14] = n * 8;
        md5cycle(state, tail);
        return state;
      }
      function md5blk(s) {
        var md5blks = [], i;
        for (i = 0; i < 64; i += 4) {
          md5blks[i >> 2] = s.charCodeAt(i) + (s.charCodeAt(i + 1) << 8) + (s.charCodeAt(i + 2) << 16) + (s.charCodeAt(i + 3) << 24);
        }
        return md5blks;
      }
      var hex_chr = '0123456789abcdef'.split('');
      function rhex(n) { var s = '', j = 0; for (; j < 4; j++) s += hex_chr[(n >> (j * 8 + 4)) & 0x0F] + hex_chr[(n >> (j * 8)) & 0x0F]; return s; }
      function hex(x) { for (var i = 0; i < x.length; i++) x[i] = rhex(x[i]); return x.join(''); }
      function add32(a, b) { return (a + b) & 0xFFFFFFFF; }
      if (str.match(/^[\s\S]*$/) && str.length < 70000) {
        return hex(md51(str));
      }
      return null;
    };

    const hash = computeMd5(email.trim().toLowerCase());
    if (hash && !cancelled) {
      setGravatarUrl(`https://www.gravatar.com/avatar/${hash}?s=${sizePx}&d=404`);
    }
    return () => { cancelled = true; };
  }, [email, profileUrl, sizePx]);

  // 1. Uploaded photo or Google profile picture
  if (profileUrl) {
    return (
      <div className={`${sizeClass} ${className} relative inline-block flex-shrink-0`}>
        <div className="w-full h-full rounded-full overflow-hidden relative bg-teal-50 dark:bg-teal-900/20">
          <div className="absolute inset-0 rounded-full pointer-events-none border-[2px] border-teal-200 dark:border-teal-800"></div>
          <img
            src={profileUrl}
            alt={user?.full_name || 'User'}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              e.target.onerror = null;
              if (gravatarUrl && e.target.dataset.triedGravatar !== 'true') {
                e.target.dataset.triedGravatar = 'true';
                e.target.src = gravatarUrl;
              } else {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div
            className="absolute inset-0 items-center justify-center bg-gradient-to-br from-teal-400 to-teal-600 rounded-full hidden"
            style={{ display: 'none' }}
          >
            <span className={`text-white font-bold ${textSize}`}>
              {getInitials(user?.full_name)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // 2. Gravatar from email
  if (gravatarUrl) {
    return (
      <div className={`${sizeClass} ${className} relative inline-block flex-shrink-0`}>
        <div className="w-full h-full rounded-full overflow-hidden relative bg-teal-50 dark:bg-teal-900/20">
          <div className="absolute inset-0 rounded-full pointer-events-none border-[2px] border-teal-200 dark:border-teal-800"></div>
          <img
            src={gravatarUrl}
            alt={user?.full_name || 'User'}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div
            className="absolute inset-0 items-center justify-center bg-teal-50 dark:bg-teal-900/20 rounded-full hidden"
            style={{ display: 'none' }}
          >
            <svg className="w-[70%] h-[70%] block" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="35" r="18" className="fill-teal-300 dark:fill-teal-700" />
              <rect x="44" y="52" width="12" height="6" rx="2" className="fill-teal-300 dark:fill-teal-700" />
              <path d="M 20 85 Q 35 60 50 60 Q 65 60 80 85 L 80 100 L 20 100 Z" className="fill-teal-300 dark:fill-teal-700" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // 3. Silhouette fallback
  return (
    <div className={`${sizeClass} ${className} relative inline-block flex-shrink-0`}>
      <div className="w-full h-full rounded-full overflow-hidden relative flex items-center justify-center bg-teal-50 dark:bg-teal-900/20">
        <div className="absolute inset-0 rounded-full pointer-events-none border-[2px] border-teal-200 dark:border-teal-800"></div>
        <svg className="w-[70%] h-[70%] block" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="35" r="18" className="fill-teal-300 dark:fill-teal-700" />
          <rect x="44" y="52" width="12" height="6" rx="2" className="fill-teal-300 dark:fill-teal-700" />
          <path d="M 20 85 Q 35 60 50 60 Q 65 60 80 85 L 80 100 L 20 100 Z" className="fill-teal-300 dark:fill-teal-700" />
        </svg>
      </div>
    </div>
  );
};

export default UserAvatar;
